import React from 'react';
import PropTypes from 'prop-types';
import Flexbox from 'flexbox-react';
import { connect } from 'react-redux';
import { Button } from 'antd';
import { Modal } from 'react-bootstrap';
import { css, cx } from 'emotion';
import AbstractFilteredList from 'components/core/AbstractFilteredList';
import ListItem from 'components/core/ListItem';
import SecurityService from 'services/SecurityService';
import ProcessService from 'services/ProcessService';
import ModalsService from 'services/ModalsService';
import ComponentUtils from 'utils/ComponentUtils';
import InfoIcon from 'components/core/InfoIcon';
import InfoMessages from 'components/core/help/InfoMessages';
import { colorVeryLightGrey, colorBorderGrey, colorLightGrey, colorVeryLightYellow, darkFont } from 'styles/base.js';
import _ from 'lodash';

const styles = css`
    .modal-dialog .modal-body {
        padding-bottom: 15px !important;
        .security-form {
            height: calc(100vh - 300px);
            .buttons-column {
                padding: 50px 15px 0 15px;
            }
            .section-title {
                ${darkFont}
                margin-bottom: 3px;
                font-size: 12px;
            }
            .section-body {
                padding: 10px;
                background-color: ${colorVeryLightGrey};
                border: 1px solid ${colorBorderGrey};
                border-radius: 0 3px 3px 3px;
                overflow: hidden;
            }
            .filtered-list-container {
                background-color:white;
                border: 1px solid ${colorBorderGrey};
                .filtered-list .items {
                    overflow-y: auto;
                    overflow-x: hidden;
                    height: calc(100vh - 410px);
                }
            }
            .filter-wrapper {
                margin: 5px;
                .filter-input {
                    align-items: stretch;
                    padding: 5px;
                    background-color: ${colorVeryLightYellow};
                    border: 1px solid ${colorLightGrey};
                }
                .found {
                    margin: 5px 5px 0 5px;
                    font-weight: bold;
                }
            }

            .variable-item {
                padding: 4px 8px;
            }

            .variable-list-items {
                overflow: auto;
            }
        }
    }
`;

const arrowButtonStyles = css`
    &.ant-btn {
        height:38px;
        margin-bottom:10px;
    }
`;

class PermissionsModal extends React.Component {
    static propTypes = {
        processId: PropTypes.string,
        users: PropTypes.object, // array of user objects
        processOwnersById: PropTypes.object,
        isSaving: PropTypes.bool
    };

    constructor(props) {
        super(props);

        this.state = {
            owners: null, // initialize to null so we know when to show loading spinner
            availableUsers: null,
            ownersHaveBeenEdited: false
        };

    }

    static getDerivedStateFromProps(props, prevState) {

        if (!prevState.ownersHaveBeenEdited) {
            let availableUsers = {};
            if (props.users) {
                availableUsers = { ...props.users };
            }

            let owners = props.processOwnersById[props.processId];
            if (owners) {
                owners = owners.reduce((ownersObj, user) => {
                    let userObj = user;
                    if (!_.isEmpty(availableUsers)) {
                        //retain email, first name, last name attributes from available users list
                        if (availableUsers[user]) {
                            userObj = availableUsers[user];
                            //user shouldn't be an option if they are already an owner
                            delete availableUsers[user];
                        } else {
                            userObj = user;
                        }
                    }
                    ownersObj[user] = userObj;
                    return ownersObj;
                }, {});
            } else {
                owners = null;
            }

            return {
                owners,
                availableUsers
            };
        }

        return {};
    }

    /**
     * Gets the owners in a null-safe manner
     * @returns {object}
     */
    getOwners() {
        return this.state.owners || {};
    }

    getAvailableUsers() {
        return this.state.availableUsers;
    }

    canEditPermissions() {
        //can only edit permissions if user has permissions and users and current owners have loaded, and is not in saving state
        // get state directly instead of call getOwners() because we need to distinguish between null (not loaded) vs
        // {} (loaded, but empty)
        const owners = this.state.owners;
        return SecurityService.canCurrentUserEditProcess(this.props.processId) && (owners !== null || this.state.ownersHaveBeenEdited) && (!_.isEmpty(this.props.users)) && !this.props.isSaving;
    }

    onAddUsers = () => {
        if (this.canEditPermissions()) {
            const selection = this.userListComponent.getSelectedItems();
            const owners = this.getOwners();
            const availableUsers = this.getAvailableUsers();

            Object.keys(selection).forEach(username => {
                owners[username] = availableUsers[username];
                delete availableUsers[username];
            });

            const ownersHaveBeenEdited = true;
            this.setState({ owners, availableUsers, ownersHaveBeenEdited });
            this.userListComponent.clearSelection();
        }
    };

    onRemoveUsers = () => {
        if (this.canEditPermissions()) {
            const selection = this.ownerListComponent.getSelectedItems();
            const owners = this.getOwners();
            const availableUsers = this.getAvailableUsers();

            Object.keys(selection).forEach(username => {
                availableUsers[username] = owners[username];
                delete owners[username];
            });

            const ownersHaveBeenEdited = true;
            this.setState({ owners, availableUsers, ownersHaveBeenEdited });
            this.ownerListComponent.clearSelection();
        }
    };

    onSaveClicked = () => {
        const owners = this.getOwners();
        if (this.canEditPermissions()) {
            if (Object.keys(owners).length > 0) {
                ProcessService.saveProcessOwners(this.props.processId, Object.keys(this.getOwners()));
            } else {
                console.log('hey you need at least one owner');
            }
        }
    };

    onCancelClicked = () => {
        ComponentUtils.closePermissionsModal();
    };

    renderUserList = () => {
        const userListRefFunction = (component) => { this.userListComponent = component; };
        const users = this.getAvailableUsers();

        let userList = null;
        if (!_.isEmpty(this.props.users)) {
            userList = (
                <UserList
                    id="allUsers"
                    sortedIds={Object.keys(users).sort()}
                    data={users}
                    filterable
                    ref={userListRefFunction}
                />
            );
        } else {
            userList = (
                <Flexbox flexDirection="row" alignItems="center">
                    <div className="spinner"/>
                    <div style={{ marginLeft: '5px' }}>Loading...</div>
                </Flexbox>
            );
        }

        return userList;
    };

    renderOwnerList = () => {
        const ownerListRefFunction = (component) => { this.ownerListComponent = component; };
        // get owners directly from state because we need to distinguish between null vs empty object which we
        // need everywhere else
        const owners = this.state.owners;

        let userList = null;
        if (owners !== null || this.state.ownersHaveBeenEdited) {
            userList = (
                <UserList
                    id="allOwners"
                    sortedIds={Object.keys(owners).sort()}
                    data={owners}
                    filterable
                    ref={ownerListRefFunction}
                />
            );
        } else {
            userList = (
                <Flexbox flexDirection="row" alignItems="center">
                    <div className="spinner"/>
                    <div style={{ marginLeft: '5px' }}>Loading...</div>
                </Flexbox>
            );
        }

        return userList;
    };

    render = () => {
        let btnDisabled = false;
        if (!this.canEditPermissions()) {
            btnDisabled = true;
        }

        let savingStyle = null;
        if (this.props.isSaving) {
            btnDisabled = true;
            savingStyle = css`cursor:wait;`;
        }

        const owners = this.getOwners();
        let saveDisabled = false;
        if (Object.keys(owners).length < 1) {
            saveDisabled = true;
        }

        return (
            <div className={cx('static-modal', styles)}>
                <Modal.Dialog ref="dialog">
                    <Modal.Header>
                        <Modal.Title>Process Permissions: {this.props.processId}</Modal.Title>
                    </Modal.Header>

                    <Modal.Body>
                        <Flexbox flexDirection="column" className="security-form">
                            <Flexbox flexDirection="row" height="100%">
                                <Flexbox flexDirection="column" flexGrow={1} className="section-body" width="42%">
                                    <Flexbox flexDirection="row" justifyContent="space-between">
                                        <div className="section-title">Available Users</div>
                                        <InfoIcon
                                            message={InfoMessages.availableUsersHelp}
                                            icon="fa-question-circle"
                                        />
                                    </Flexbox>
                                    {this.renderUserList()}
                                </Flexbox>
                                <Flexbox flexDirection="column" width="16%" className="buttons-column">
                                    <Button type="primary" onClick={this.onAddUsers} disabled={btnDisabled} className={arrowButtonStyles}>
                                        <i className="fa fa-2x fa-arrow-right" aria-hidden />
                                    </Button>
                                    <Button type="primary" onClick={this.onRemoveUsers} disabled={btnDisabled} className={arrowButtonStyles}>
                                        <i className="fa fa-2x fa-arrow-left" aria-hidden />
                                    </Button>
                                </Flexbox>
                                <Flexbox flexDirection="column" flexGrow={1} className="section-body" width="42%">
                                    <div className="section-title">Process Owners</div>
                                    {this.renderOwnerList()}
                                </Flexbox>
                            </Flexbox>
                        </Flexbox>
                    </Modal.Body>

                    <Modal.Footer>
                        <Button onClick={this.onSaveClicked} type="primary" disabled={btnDisabled || saveDisabled} className={savingStyle}>
                            Save
                        </Button>
                        <Button autoFocus onClick={this.onCancelClicked}>
                            Cancel
                        </Button>
                    </Modal.Footer>

                </Modal.Dialog>
            </div>
        );
    }
}

class UserList extends AbstractFilteredList {

    //overriding parent's onDocumentClick so that when clicks are made outside of list selections aren't lost
    onDocumentClick = (e) => {};

    getSelectedItems() {
        return this.state.selectedItems;
    }

    match(regex, name, value) {
        let searchStr = name;
        if (value) {
            searchStr = `${name} ${value.firstName} ${value.lastName}`;
        }
        return (searchStr.search(regex) > -1);
    }

    renderItem(name, value, selected) {
        let child = '';
        if (value.firstName && value.lastName) {
            child = `(${value.firstName} ${value.lastName})`;
        }

        return (
            <ListItem
                key={name}
                name={name}
                selected={selected}
                className={this.getItemClassName()}
                onClick={this.onClick}
            >
                {this.renderEntity(name, child)}
            </ListItem>
        );
    }
}

// Automatically pull state information from the store and
// pass it to this component as props
const mapStateToProps = (storeState) => {
    const processState = ProcessService.getMyState(storeState);
    const securityState = SecurityService.getMyState(storeState);
    const modalsState = ModalsService.getMyState(storeState);
    return {
        users: securityState.users,
        processOwnersById: processState.processOwnersById,
        isSaving: modalsState.permissionsModal.isSaving
    };
};

export default connect(mapStateToProps, null, null/*, { withRef: true }*/)(PermissionsModal);




// WEBPACK FOOTER //
// ./src/components/core/modals/PermissionsModal.js